<template>
  <div class="home home--v2 container-fluid">
    <div class="row home-grid">
      <!-- LEFT (40%) -->
      <div class="col-lg-5">
        <!-- User profile -->
        <section class="panel panel--profile">
          <div class="panel__body panel__body--profile">
            <div class="profile-compact" style="min-height: 52px;">
              <div class="logo-container">
                <img
                    v-bind:src="getProfileAvatar"
                    class="avatar-img--sm"
                    width="30"
                    height="30"
                    qid="profile-photo"
                    id="avatar"
                    alt="Avatar"
                />
                <div class="upload-helper-home rounded-circle">
                  <label class="mt-3 upload">
                    <input type="file"
                           name="fileupload"
                           value="fileupload"
                           id="file"
                           @change="onFileChange"
                    >
                    <font-awesome-icon icon="cloud-upload-alt" class="cursor-pointer" />
                  </label>
                </div>
              </div>

              <div class="profile-compact__info">
                <div class="profile-compact__top">
                  <div class="profile-compact__text">
                    <!-- Name (editable) -->
                    <span
                        qid="profile-name"
                        class="profile-compact__name"
                        @dblclick="showNameInputFunction"
                        @keydown.esc="hideNameInputFunction"
                        v-if="!showNameInput"
                        v-b-tooltip.hover
                        :title="$t('system.click_to_edit')"
                    >
                      {{ user.name }}
                    </span>

                    <b-form-input
                        qid="homepage-user-name"
                        type="text"
                        v-model="userName"
                        v-if="showNameInput"
                        ref="nameInput"
                        v-on:keyup.enter="saveName"
                        @keydown.esc="hideNameInputFunction"
                        @focusout="hideNameInputFunction"
                        class="profile-compact__input"
                    />

                    <span
                        data-qid="profile-email"
                        class="section-compact__secondary">
                      {{ user.email }}
                    </span>
                  </div>

                  <!-- Inline icon-only actions -->
                  <div class="profile-compact__actions">
                    <router-link
                        class="icon-btn"
                        to="/notifications"
                        qid="notifications-page-link"
                        v-b-tooltip.hover
                        :title="$t('notifications.view_all')"
                    >
                      <font-awesome-icon icon="bell" />
                      <!-- <span v-if="unreadCount" class="dot"></span> -->
                    </router-link>

                    <router-link
                        class="icon-btn"
                        :to="{ name: 'profile', params: { slug: getUser.slug } }"
                        qid="home-activities"
                        v-b-tooltip.hover
                        :title="$t('system.view_my_activities')"
                    >
                      <font-awesome-icon icon="wave-square" />
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Company profile -->
        <section
            v-if="isGranted(permissions.USER_VIEW)"
            class="panel panel--profile"
        >
          <div class="panel__body panel__body--profile">
            <div class="profile-compact" style="min-height: 52px;">
              <Avatar
                  :object="getClient"
                  width="30"
                  height="30"
                  class="client-picker__avatar user-menu__client-avatar"
              />

              <div class="profile-compact__info">
                <div class="profile-compact__top">
                  <div class="profile-compact__text">
                    <!-- Name (kept editable) -->
                    <span
                        data-qid="profile-name"
                        class="profile-compact__name"
                    >
                      {{ getClient.name }}
                    </span>

                    <span
                        v-if="getClient.parent"
                        data-qid="parent"
                        class="section-compact__secondary">
                      {{ getClient.parent.name }}
                    </span>
                  </div>

                  <!-- Inline icon-only actions -->
                  <div class="profile-compact__actions">
                    <router-link
                        v-if="isGranted(permissions.MAINTENANCE_VIEW)"
                        class="icon-btn"
                        to="/maintenance/users"
                        qid="users-and-roles"
                        v-b-tooltip.hover
                        :title="$t('administration.users.users_owners_title')"
                    >
                      <font-awesome-icon icon="user-cog" />
                    </router-link>

                    <router-link
                        v-if="isGranted(permissions.MAINTENANCE_VIEW)"
                        class="icon-btn"
                        :to="{ name: 'clientProfile', params: { slug: getClient.slug }}"
                        qid="client profile"
                        v-b-tooltip.hover
                        :title="getClient.parent
                          ? $t('top_nav.user_dropdown.partition_profile')
                          : $t('top_nav.user_dropdown.client_profile')"
                    >
                      <font-awesome-icon icon="pen" />
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Hero buttons (no background section) -->
        <div class="hero-actions">
          <CreatePartitionModal
              v-if="getClient && !getClient.parent && isGranted(permissions.PARTITION_CREATE)"
              class="hero-btn"
          />

          <span
              v-if="getClient && getClient.parent && !measuresFound && isGranted(permissions.MAINTENANCE_VIEW)"
              class="hero-btn"
          >
            <router-link
                class="btn btn-secondary"
                qid="home-create-partition"
                to="/maintenance/measures"
            >
              <font-awesome-icon icon="toolbox" class="mr-2" />
              {{ $t('maintenance.measures.import_button') }}
            </router-link>
          </span>

          <CreateProjectModal
              v-if="getClient && getClient.projectModule && isGranted(permissions.PROJECT_CREATE)"
              class="hero-btn"
              @reloadContent="loadProjects()"
          />
        </div>

        <!-- My projects -->
        <section
            class="panel"
            v-if="getClient && getClient.projectModule && (navigation.project || isGranted(permissions.PROJECT_VIEW))"
        >
          <header class="panel__header">
            <div class="panel__title">
              <h4 class="mb-0">{{ $t('tasks.my_projects') }}</h4>
              <a
                  href="https://complyon.com/auditor-user-guide/"
                  class="section-compact__secondary"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <GuideButton></GuideButton>
              </a>
            </div>

            <!-- Inline icon-only actions -->
            <div class="profile-compact__actions">
              <b-button
                  variant="link"
                  class="icon-btn"
                  qid="refresh-table"
                  @click="loadProjects"
                  v-b-tooltip.hover
                  :title="$t('table.refresh_table')"
              >
                <font-awesome-icon icon="sync-alt" />
              </b-button>

              <router-link
                  class="icon-btn"
                  to="/projects/gantt"
                  qid="home-gantt-link"
                  v-b-tooltip.hover
                  :title="$t('projects.gantt_overview_list_title')"
              >
                <font-awesome-icon icon="chart-gantt" />
              </router-link>
            </div>
          </header>

          <div class="panel__body">
            <div class="card-list">
              <router-link
                  v-for="(project, index) in tableProjects"
                  :key="'project-card-' + index"
                  :id="'tableProjects-row-' + index"
                  type="button"
                  class="card-row cursor-pointer"
                  :disabled="project.status === 'closed' || project.status === 'completed'"
                  :to="{ name: 'projects.view', params: { slug: project.slug }}"
              >
                <!-- keep popover (targets the card button id) -->
                <b-popover
                    class="hide-on-mobile"
                    :target="'tableProjects-row-' + index"
                    boundary="viewport"
                    triggers="hover"
                    placement="top"
                    :delay="{ show: 500, hide: 0 }"
                >
                  <div v-if="project.description" class="mt-2">
                    <span class="text-bold">
                      {{ $t('maintenance.data_objects.table.description') }}:
                    </span>
                    <br>
                    {{ project.description | truncate(200)}}
                    <br>
                    <br>
                  </div>

                  <div v-if="project.type">
                    <span class="text-bold">
                      {{ $t('table.type') }}:
                    </span>
                    <span>
                      <br>
                      <span v-if="project.type === 'update'" class="badge">
                        <font-awesome-icon icon="sync-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.update_label') }}
                      </span>
                      <span v-if="project.type === 'validation'" class="badge">
                        <font-awesome-icon icon="check-circle" style="color:#081F26;"/>
                        {{ $t('projects.view.validation_label') }}
                      </span>
                      <span v-if="project.type === 'control'" class="badge">
                        <font-awesome-icon icon="shield-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.control_label') }}
                      </span>
                      <span v-if="project.type === 'security_measure'" class="badge">
                        <font-awesome-icon icon="shield-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.security_measure_label') }}
                      </span>
                      <span v-if="project.type === 'risk_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.risk_assessment') }}
                      </span>
                      <span v-if="project.type === 'control_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.control_assessment') }}
                      </span>
                      <span v-if="project.type === 'bia_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.bia_assessment') }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.assessment">
                    <span class="text-bold">
                      {{ $t('table.template') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ project.assessment.name }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.scope">
                    <span class="text-bold">
                      {{ $t('table.scope') }}:
                    </span>
                    <span>
                    <br>
                    <span class="badge" v-if="project.scope === 'policy'">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('projects.scope.policy') }}
                    </span>
                    <span class="badge" v-if="project.scope === 'system'">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('projects.system.label') }}
                    </span>
                    <span class="badge" v-if="project.scope === 'process'">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('projects.process.label') }}
                    </span>
                    <span class="badge" v-if="project.scope === 'activity'">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('projects.activity.label') }}
                    </span>
                    <span class="badge" v-if="project.scope === 'third_party'">
                      <font-awesome-icon icon="crosshairs"/>
                      {{ $t('projects.third_party.label') }}
                    </span>
                  </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.tags_translated && project.tags_translated.length">
                    <span class="text-bold">
                      {{ $t('processes.table.tags') }}:
                    </span>
                    <br>
                    <span v-for="(tag, tag_index) in project.tags_translated" :key="tag_index" data-qid="p-tags-list">
                      <span class="badge" :title="tag.name_translated">
                        <font-awesome-icon icon="tag"/>
                        {{ tag.name_translated }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.users && project.users.length">
                    <span class="text-bold">
                      {{ $t('projects.create.owners_label') }}:
                    </span>
                    <span v-for="(usr, usr_index) in project.users" :key="usr_index">
                      <br>
                      <span data-qid="profile-photo">
                        <Avatar :object="usr" size="xs"></Avatar><span> {{ usr.name }}</span>
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.data_collection_end_date">
                    <span class="text-bold">
                      {{ $t('projects.schedule.data_due_date_label') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge">
                        <font-awesome-icon icon="calendar-alt"/>
                        {{ project.data_collection_end_date | formatDate }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="project.end_date">
                    <span class="text-bold">
                      {{ $t('projects.schedule.due_date_label') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge">
                        <font-awesome-icon icon="calendar-alt"/>
                        {{ project.end_date | formatDate }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>
                </b-popover>

                <div class="card-row__main">
                  <div class="card-row__title">
                    <span class="text-truncate">
                      {{ project.title }}
                      <font-awesome-icon
                          v-if="isOwner(project)"
                          icon="up-right-from-square"
                          class="ml-1 text-success"
                      />
                    </span>
                    <span class="card-row__meta hide-on-mobile">
                      <span class="due-pill" :class="dueClass(project.end_date)">
                        <font-awesome-icon icon="calendar-alt" class="mr-1" />
                        <span v-if="project.end_date">{{ project.end_date | formatDate }}</span>
                        <span v-else>-</span>
                      </span>
                    </span>
                  </div>
                </div>

                <div class="card-row__right">
                  <vm-progress
                      type="circle"
                      :percentage="project.progress"
                      :width="34"
                      :stroke-width="2"
                      stroke-color="#00c29e"
                  >
                    {{ project.progress }}%
                  </vm-progress>

                  <!-- Inline icon-only actions -->
                  <div class="profile-compact__actions">
                    <b-button
                        v-if="project.status !== 'closed' && project.status !== 'completed'"
                        variant="link"
                        class="icon-btn"
                        qid="show-project-progress"
                        v-b-tooltip.hover
                        :title="$t('system.view_project')"
                        @click.stop.prevent="showTask(project)"
                    >
                      <font-awesome-icon icon="chart-gantt" />
                    </b-button>
                  </div>
                </div>
              </router-link>

              <div
                  v-if="!loadingOpenProjects && !tableProjects.length"
                  class="empty"
              >
                <h5 class="mb-0">{{ $t('system.no_results') }}</h5>
              </div>

              <div v-if="loadingOpenProjects" class="empty">
                <div class="mt-2 mb-2">
                  <div class="swing-container">
                    <div class="swing">
                      <div class="swing-l"></div>
                      <div></div><div></div><div></div>
                      <div class="swing-r"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-body">
              <div class="pagination row">
                <div class="col-md-4">
                  <div class="row">
                    <div
                        class="float-left hide-on-mobile"
                        style="width: 80px;"
                    >
                      <multiselect
                          v-model="perPageProjects"
                          :options="perPageOptions"
                          track-by="value"
                          label="value"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          qid="my-projects-per-page"
                          @select="perPageChangedProjects"
                          :placeholder="selectedPerPageProjects"
                          @remove="perPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-8">
                  <b-pagination
                      class="float-right"
                      v-model="currentPageProjects"
                      :total-rows="rowsProjects"
                      :per-page="selectedPerPageProjects"
                      aria-controls="my-table"
                      @change="pageChangedProjects"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{getPerPageProjects}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <ProjectViewModal
          :slug="selectedProjectSlug"
          :showModal="viewProjectModal"
          @closeViewModal="closeViewProjectModal"
      ></ProjectViewModal>


      <!-- RIGHT (60%) -->
      <div class="col-lg-7">

        <!-- MY TASKS SECTION -->
        <section class="panel">
          <header class="panel__header">
            <div class="panel__title">
              <h4 class="mb-0">
                {{ $t('tasks.my_tasks') }}
              </h4>
              <a
                  href="https://complyon.com/documentation_collection_guide/"
                  class="section-compact__secondary"
                  target="_blank"
                  rel="noopener noreferrer"
              >
                <GuideButton></GuideButton>
              </a>
            </div>

            <!-- Inline icon-only actions -->
            <div class="profile-compact__actions">
              <b-button
                  variant="link"
                  class="icon-btn"
                  qid="refresh-table"
                  @click="loadTasks()"
                  v-b-tooltip.hover
                  :title="$t('table.refresh_table')"
              >
                <font-awesome-icon icon="sync-alt" />
              </b-button>

              <b-button
                  variant="link"
                  class="icon-btn"
                  @click="handleFilterButton(false)"
                  qid="home-tasks-filter"
                  v-b-tooltip.hover
                  :title="$t('tasks.table.task_filters')"
              >
                <font-awesome-icon icon="filter" />
              </b-button>
            </div>
          </header>

          <div class="panel__body">
            <!-- Task filter -->
            <b-collapse :visible="filterOpened" class="mb-3" id="task-filter">
              <div class="filters">
                <div class="row">
                  <div class="col-md-4">
                    <b-form-group>
                      <label>{{ $t('projects.table.type') }}</label>
                      <multiselect
                          v-model="filters.type"
                          @select="debounceInput"
                          @remove="debounceInput"
                          :placeholder="$t('projects.table.type')"
                          :options="types"
                          :multiple="false"
                          :taggable="false"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          track-by="value"
                          label="name"
                      />
                    </b-form-group>
                  </div>

                  <div class="col-md-4">
                    <b-form-group>
                      <label>{{ $t('projects.table.scope') }}</label>
                      <multiselect
                          v-model="filters.scope"
                          :options="scopes"
                          :placeholder="$t('projects.table.scope')"
                          @select="debounceInput"
                          @remove="debounceInput"
                          :multiple="false"
                          :taggable="false"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          track-by="value"
                          label="name"
                      />
                    </b-form-group>
                  </div>

                  <div class="col-md-4">
                    <b-form-group>
                      <label>{{ $t('system.status') }}</label>
                      <multiselect
                          v-model="filters.status"
                          :options="statuses"
                          :placeholder="$t('system.status')"
                          @select="debounceInput"
                          @remove="debounceInput"
                          :multiple="false"
                          :taggable="false"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          track-by="value"
                          label="name"
                      />
                    </b-form-group>
                  </div>
                </div>

                <div class="text-right">
                  <a href="#" @click.prevent="resetFilters" class="text-muted">{{ $t('system.reset_filters') }}</a>
                </div>
              </div>
            </b-collapse>

            <div
                v-if="totalRows"
                class="card-list">
              <div
                  v-for="(object, index) in tableTasks"
                  :key="'task-card-' + index"
                  :id="'tableTasks-row-' + index"
                  type="button"
                  class="card-row cursor-pointer"
                  @click="openViewProjectModal(object)"
              >
                <b-popover
                    class="hide-on-mobile"
                    :target="'tableTasks-row-' + index"
                    boundary="viewport"
                    triggers="hover"
                    placement="top"
                    :delay="{ show: 500, hide: 0 }"
                >
                  <div v-if="object.description" class="mt-2">
                    <span class="text-bold">
                      {{ $t('maintenance.data_objects.table.description') }}:
                    </span>
                    <br>
                    {{ object.description | truncate(200)}}
                    <br>
                    <br>
                  </div>

                  <div v-if="object.type">
                    <span class="text-bold">
                      {{ $t('table.type') }}:
                    </span>
                    <span>
                      <br>
                      <span v-if="object.type === 'update'" class="badge">
                        <font-awesome-icon icon="sync-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.update_label') }}
                      </span>
                      <span v-if="object.type === 'validation'" class="badge">
                        <font-awesome-icon icon="check-circle" style="color:#081F26;"/>
                        {{ $t('projects.view.validation_label') }}
                      </span>
                      <span v-if="object.type === 'control'" class="badge">
                        <font-awesome-icon icon="shield-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.control_label') }}
                      </span>
                      <span v-if="object.type === 'security_measure'" class="badge">
                        <font-awesome-icon icon="shield-alt" style="color:#081F26;"/>
                        {{ $t('projects.view.security_measure_label') }}
                      </span>
                      <span v-if="object.type === 'risk_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.risk_assessment') }}
                      </span>
                      <span v-if="object.type === 'control_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.control_assessment') }}
                      </span>
                      <span v-if="object.type === 'bia_assessment'" class="badge">
                        <font-awesome-icon icon="file-alt" style="color:#081F26;"/>
                        {{ $t('maintenance.assessments.type.bia_assessment') }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="object.scope">
                    <span class="text-bold">
                      {{ $t('table.scope') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge" v-if="object.scope === 'policy'">
                        <font-awesome-icon icon="crosshairs"/>
                        {{ $t('projects.scope.policy') }}
                      </span>
                      <span class="badge" v-if="object.scope === 'system'">
                        <font-awesome-icon icon="crosshairs"/>
                        {{ $t('projects.system.label') }}
                      </span>
                      <span class="badge" v-if="object.scope === 'process'">
                        <font-awesome-icon icon="crosshairs"/>
                        {{ $t('projects.process.label') }}
                      </span>
                      <span class="badge" v-if="object.scope === 'activity'">
                        <font-awesome-icon icon="crosshairs"/>
                        {{ $t('projects.activity.label') }}
                      </span>
                      <span class="badge" v-if="object.scope === 'third_party'">
                        <font-awesome-icon icon="crosshairs"/>
                        {{ $t('projects.third_party.label') }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="object.users && object.users.length">
                    <span class="text-bold">
                      {{ $t('projects.create.owners_label') }}:
                    </span>
                    <span v-for="(usr, usr_index) in object.users" :key="usr_index">
                      <br>
                      <span>
                        <Avatar :object="usr" size="xs"></Avatar><span> {{ usr.name }}</span>
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="object.data_collection_end_date && object.user_progress.data_task_count">
                    <span class="text-bold">
                      {{ $t('projects.schedule.data_due_date_label') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge">
                        <font-awesome-icon icon="calendar-alt"/>
                        {{ object.data_collection_end_date | formatDate }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>

                  <div v-if="object.end_date && object.user_progress.task_count">
                    <span class="text-bold">
                      {{ $t('projects.schedule.due_date_label') }}:
                    </span>
                    <span>
                      <br>
                      <span class="badge">
                        <font-awesome-icon icon="calendar-alt"/>
                        {{ object.end_date | formatDate }}
                      </span>
                    </span>
                    <br>
                    <br>
                  </div>
                </b-popover>

                <div class="card-row__main">
                  <div class="card-row__title">
                    <span class="text-truncate">
                      {{ object.title }}
                      <font-awesome-icon icon="eye" class="ml-1 text-success" />
                    </span>

                    <span class="card-row__meta hide-on-mobile">
                      <span class="due-pill" :class="dueClass(object.data_collection_end_date)">
                        <font-awesome-icon icon="calendar-alt" class="mr-1" />
                        <span v-if="object.data_collection_end_date">{{ object.data_collection_end_date | formatDate }}</span>
                        <span v-else>-</span>
                      </span>
                    </span>
                  </div>
                </div>

                <div class="card-row__right">
                  <vm-progress
                      type="circle"
                      :percentage="object.user_progress.progress"
                      :width="34"
                      :stroke-width="2"
                      stroke-color="#00c29e"
                  >
                    {{ object.user_progress.progress }}%
                  </vm-progress>
                </div>
              </div>
            </div>

            <div
                v-if="!loadingOpen && !totalRows"
                class="empty"
            >
              <h5 class="mb-0">{{ $t('system.no_results') }}</h5>
            </div>

            <div v-if="loadingOpen" class="empty pt-3">
              <div class="mt-2 mb-2">
                <div class="swing-container">
                  <div class="swing">
                    <div class="swing-l"></div>
                    <div></div><div></div><div></div>
                    <div class="swing-r"></div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card-body">
              <div class="pagination row">
                <div class="col-md-4">
                  <div class="row">
                    <div
                        class="float-left hide-on-mobile"
                        style="width: 80px;"
                    >
                      <multiselect
                          v-model="perPage"
                          :options="perPageOptions"
                          track-by="value"
                          label="value"
                          select-label=""
                          selected-label=""
                          deselect-label=""
                          qid="my-projects-per-page"
                          @select="perPageChanged"
                          :placeholder="selectedPerPage"
                          @remove="perPageRemoved"
                      ></multiselect>
                    </div>
                  </div>
                </div>
                <div class="col-md-8">
                  <b-pagination
                      class="float-right"
                      v-model="currentPage"
                      :total-rows="rows"
                      :per-page="selectedPerPage"
                      aria-controls="my-table"
                      @change="pageChangedProjects"
                      limit="1"
                  >
                    <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                    <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                    <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                    <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                    <div slot="ellipsis-text">
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                      <b-spinner small type="grow"></b-spinner>
                    </div>
                    <span class="text-muted" slot="page" slot-scope="{ page, active }">
                      <span v-if="active">{{getPerPage}}</span>
                    </span>
                  </b-pagination>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Rejected validations (same card-list table layout) -->
        <section
            class="panel mt-4"
            v-if="getClient && getClient.id && (getClient.processingActivitiesModule || getClient.systemModule || getClient.contractBasicModule) && validationCount"
        >
          <header class="panel__header">
            <div class="panel__title">
              <h4 class="mb-0">
                {{ $t('system.rejected_validations_title') }}
                <span v-if="validationCount" class="pill ml-2">{{ validationCount }}</span>
              </h4>
              <p class="panel__subtitle mb-0">
                {{ $t('system.rejected_validations_subtitle') || 'Items that need your review' }}
              </p>
            </div>

            <div class="panel__actions">
              <b-button variant="light" class="btn btn-light btn-soft" @click="validationsTabClicked">
                <font-awesome-icon icon="rotate" class="mr-2" />
                {{ $t('system.refresh') || 'Refresh' }}
              </b-button>
            </div>
          </header>

          <div class="panel__body">
            <!-- If RejectedValidations is currently a full widget/table, keep it.
                 To unify layout, ideally expose `rejectedValidations` list and a click handler.
                 For now, render the component; if you share its internals, I’ll convert it to the same card-list. -->
            <RejectedValidations v-if="showValidationsTab"></RejectedValidations>
            <RejectedValidations v-else></RejectedValidations>
          </div>
        </section>
      </div>
    </div>


    <!-- MODAL SECTION -->

    <!-- CROP IMAGE MODAL -->
    <b-modal ref="crop-image" hide-footer size="lg">
      <div class="d-block text-center">
        <h4 data-qid="edit-profile-modal-title">{{ $t('Crop image') }}</h4>
      </div>
      <div class="form-container" style="max-height: 600px;">
        <img :src="imageToCrop" qid="profile-photo" id="crop" />
      </div>
      <div class="text-right">
        <b-button
            class="mt-3"
            variant="secondary"
            @click="hideCropModal"
            qid="edit-profile-cancel-button">
          {{ $t('profile.password.cancel') }}
        </b-button>
        <b-button
            type="submit"
            class="mt-3 ml-3"
            variant="success"
            id="crop-button"
            :disabled="buttonDisabled"
            qid="edit-profile-submit-button"
        >
          <span v-if="buttonDisabled" class="mr-1">
            <font-awesome-icon icon="spinner" size="sm" class="fa-spin" />
          </span>
          {{ $t('profile.password.submit') }}
        </b-button>
      </div>
    </b-modal>


    <!-- PROJECT TASKS MODAL -->
    <b-modal
        ref="view-project"
        @hide="hideViewProjectModal"
        size="xl"
        modal-class="task-modal"
        :hide-header="selectedProject.type === 'control_assessment'"
        hide-footer
    >
      <template #modal-header="{ close }">
        <div class="text-center" style="width: 100%;">
          <span @click="hideViewProjectModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times"/>
          </span>
          <h5 class="mb-2" v-if="selectedProject">
            {{ $t('projects.tasks_modal.title') }}
            <router-link
                :to="{ name: 'projects.view', params: { slug: selectedProject.slug }}"
                qid="s-view-page-link"
                class="breadcrumb-item ml-2"
                v-if="isOwner(selectedProject)"
            >
              <font-awesome-icon icon="calendar-alt" class="mr-1 breadcrumb-item"/>
              {{ selectedProject.title }}
            </router-link>
            <span v-if="!isOwner(selectedProject)" class="ml-2 text-success breaducrumb-item" style="pointer-events:none;">
              <font-awesome-icon icon="calendar-alt" class="mr-1"/>
              {{ selectedProject.title }}
            </span>
          </h5>
        </div>
      </template>

      <!-- Asset tasks -->
      <div v-if="selectedProject.type !== 'control_assessment'">
        <table class="table table-responsive-md table-list-secondary">
          <thead>
            <tr>
              <th>
                {{ $t('projects.table.name') }}
              </th>
              <th>
                {{ $t('table.asset') }}
              </th>
              <th>
                {{ $t('tasks.table.task_assignee') }}
              </th>
              <th>
                {{ $t('system.status') }}
              </th>
              <th style="width: 320px"></th>
            </tr>
          </thead>

          <tbody
              v-if="open_tasks && open_tasks.length"
          >
            <tr
                v-for="(object, index) in open_tasks"
                :key="index"
            >
              <td>
                  <span v-if="object.code === 'business_risk_assessment' || object.code === 'privacy_risk_assessment' || object.code === 'system_impact_assessment'">
                    {{ $t('maintenance.assessments.system_impact_assessment_label') }}
                  </span>
                <span v-else>
                    <span v-if="selectedProject.type && (selectedProject.type === 'update' || selectedProject.type === 'validation')">
                      {{ $t('tasks.values.' + $options.filters.slugify(object.title)) }}
                    </span>
                    <span v-else>
                      <span v-if="selectedProject.type && selectedProject.type === 'control_assessment'">
                        {{ object.full_title}}
                      </span>
                      <span v-else>
                        {{ object.title}}
                      </span>
                    </span>
                  </span>
              </td>

              <td>
                <span v-if="object.model" class="text-normal">
                  <font-awesome-icon
                      icon="desktop"
                      size="lg"
                      class="mr-1"
                      v-if="object.model.who_am_i === 'App\\System\\Models\\System'"/>
                  <font-awesome-icon
                      icon="cubes"
                      size="lg"
                      class="mr-1"
                      v-if="object.model.who_am_i === 'App\\Activity\\Models\\Activity'"/>
                  <font-awesome-icon
                      icon="cube"
                      size="lg"
                      class="mr-1"
                      v-if="object.model.who_am_i === 'App\\Process\\Models\\Process'"/>
                  <font-awesome-icon
                      icon="file-signature"
                      size="lg"
                      class="mr-1"
                      v-if="object.model.who_am_i === 'App\\Maintenance\\ThirdParty\\Models\\ThirdParty'"/>
                  <font-awesome-icon
                      icon="file-invoice"
                      size="lg"
                      class="mr-1"
                      v-if="object.model.who_am_i === 'App\\Maintenance\\Policy\\Models\\Policy'"/>
                  {{ object.model.name }}
                </span>
                <span v-if="!object.model" class="text-normal">
                  {{ $t('tasks.modal.content_deleted') }}
                </span>
              </td>

              <td>
                  <span class="mr-1" v-for="(user, index) in object.users" :key="index">
                    <span data-qid="profile-photo">
                      <Avatar :object="user" size="xs"></Avatar>
                    </span>
                  </span>
              </td>

              <td>
                  <span v-if="object.status === 'completed'" >
                    <font-awesome-icon icon="check-circle" class="icon-success"/>
                  </span>
                <span v-if="object.status === 'pending'" class="pr-2">
                    <font-awesome-icon
                        icon="exclamation-triangle"
                        class="risk-6"
                    />
                  </span>
                <span class="text-normal">
                    {{ $t('system.' + $options.filters.slugify(object.status)) }}
                  </span>
              </td>

              <td class="text-right">
                <b-button class="ml-2"
                          variant="light"
                          @click="showEditUsersModal(object)"
                          qid="delete-s-cancel-button"
                          v-if="object.status === 'pending' && (isOwner(object) || isDataOwner(object) || isOwner(selectedProject))"
                >
                  {{ $t('tasks.re_assign_button') }}
                </b-button>
                <AssessmentTaskModal
                    class=""
                    :task="object"
                    :tasks="selectedProject"
                    :fromHomepage="true"
                    @reloadContent="loadProjectTasks()"
                    v-if="(selectedProject.type === 'risk_assessment' || selectedProject.type === 'bia_assessment') && object.status === 'pending'"
                ></AssessmentTaskModal>
                <AssessmentGlobalSingleTaskModal
                    class=""
                    :task="object"
                    :tasks="selectedProject"
                    :fromHomepage="true"
                    @reloadContent="loadProjectTasks()"
                    v-if="selectedProject.type === 'control_assessment' && object.status === 'pending'"
                ></AssessmentGlobalSingleTaskModal>
                <RiskTaskModal
                    class=""
                    :task="object"
                    :tasks="selectedProject"
                    :fromHomepage="true"
                    @reloadContent="loadProjectTasks()"
                    v-if="(selectedProject.type === 'control' || selectedProject.type === 'security_measure') && object.status === 'pending'"
                ></RiskTaskModal>
                <UpdateTaskModal
                    class=""
                    :task="object"
                    :tasks="selectedProject"
                    :fromHomepage="true"
                    @reloadContent="loadProjectTasks()"
                    v-if="selectedProject.type === 'update' && object.status === 'pending'"
                ></UpdateTaskModal>
                <ValidationTaskModal
                    class=""
                    :task="object"
                    :tasks="selectedProject"
                    :fromHomepage="true"
                    @reloadContent="loadProjectTasks()"
                    v-if="selectedProject.type === 'validation' && object.status === 'pending'"
                ></ValidationTaskModal>

                <b-button class="mr-2"
                          variant="success"
                          @click="showViewTaskModal(object)"
                          qid="delete-s-cancel-button"
                          v-if="object.status === 'completed' && selectedProject.type !== 'update' && selectedProject.type !== 'validation'"
                >
                  {{ $t('tasks.view_button') }}
                </b-button>
              </td>
            </tr>
          </tbody>

          <tbody v-if="open_tasks && !open_tasks.length && !loadingTasks">
            <tr class="text-center">
              <td colspan="10">
                <h5 class="mt-3">
                  {{ $t('system.no_results') }}
                </h5>
              </td>
            </tr>
          </tbody>

          <tbody v-if="loadingTasks">
            <tr class="text-center">
              <td colspan="10">
                <div class="mt-2 mb-4">
                  <div class="swing-container">
                    <div class="swing">
                      <div class="swing-l"></div>
                      <div></div>
                      <div></div>
                      <div></div>
                      <div class="swing-r"></div>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- pagination -->
        <div class="pagination row">
          <div class="col-md-4">
            <div class="row">
              <div class="float-left pl-4">
                <multiselect
                    v-model="tasksPerPage"
                    :options="perPageOptions"
                    track-by="value"
                    label="value"
                    select-label=""
                    selected-label=""
                    deselect-label=""
                    qid="create-org-form-organization-level"
                    @select="tasksPerPageChanged"
                    :placeholder="tasksSelectedPerPage"
                    @remove="tasksPerPageRemoved"
                ></multiselect>
              </div>
            </div>
          </div>
          <div class="col-md-8">
            <b-pagination
                class="float-right"
                v-model="tasksCurrentPage"
                :total-rows="tasksRows"
                :per-page="tasksSelectedPerPage"
                aria-controls="my-table-1"
                @change="tasksPageChanged"
                limit="1"
            >
              <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
              <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
              <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
              <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
              <div slot="ellipsis-text">
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
                <b-spinner small type="grow"></b-spinner>
              </div>
              <span class="text-muted" slot="page" slot-scope="{ page, active }">
                    <span v-if="active">{{getTasksPerPage}}</span>
                  </span>
            </b-pagination>
          </div>
        </div>
      </div>


      <!-- Assessment tasks -->
      <div class="modal__body row" v-if="selectedProject.type === 'control_assessment'">
        <!-- LEFT (40%) -->
        <div class="col-lg-5">
          <!-- Project info -->
          <section class="panel panel--profile">
            <div class="panel__body panel__body--profile">
              <div class="profile-compact">
                <div class="logo-container">
                  <font-awesome-icon icon="calendar-alt" class="mr-1 breadcrumb-item"/>
                </div>

                <div class="profile-compact__info">
                  <div class="profile-compact__top">
                    <div class="profile-compact__text">
                      <span>
                        {{ selectedProject.title }}
                      </span>

                      <span
                          data-qid="scope-period"
                          class="section-compact__secondary"
                      >
                          <span v-if="selectedProject.scope_start_date">{{ selectedProject.scope_start_date | formatDate }}</span><span v-else>-</span> {{ $t('system.to') }} <span v-if="selectedProject.scope_end_date">{{ selectedProject.scope_end_date | formatDate }}</span><span v-else>-</span>
                      </span>
                    </div>

                    <!-- Inline icon-only actions -->
                    <div class="profile-compact__actions">
                      <router-link
                          v-if="isOwner(selectedProject)"
                          class="icon-btn"
                          :to="{ name: 'projects.view', params: { slug: selectedProject.slug }}"
                          qid="users-and-roles"
                          v-b-tooltip.hover
                          :title="selectedProject.title"
                      >
                        <font-awesome-icon icon="up-right-from-square" />
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Company profile -->
          <section
              class="panel panel--profile"
              style="margin-top: 15px"
          >
            <div class="profile-compact__info">
              <div class="profile-compact__top">
                <div class="profile-compact__text">
                  <span class="pl-3 pt-3">
                    {{ $t('table.due_date') }}
                  </span>
                </div>
              </div>
            </div>

            <div class="pl-3">
              <div class="profile-compact">
                <div class="card-row__main">
                  <div class="card-row__title">
                        <span class="card-row__meta">
                          <span class="due-pill" :class="dueClass(selectedProject.data_collection_end_date)">
                            <font-awesome-icon
                                icon="calendar-alt"
                                class="mr-1"
                            />
                            <span v-if="selectedProject.data_collection_end_date">
                              {{ selectedProject.data_collection_end_date | formatDate }}
                            </span>
                            <span v-else>
                              -
                            </span>
                          </span>
                        </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <!-- Task filter -->
          <section
              class="panel panel--profile"
              style="margin-top: 15px"
          >
            <div class="profile-compact__info">
              <div class="profile-compact__top">
                <div class="profile-compact__text">
                  <span class="pl-3 pt-3">
                    {{ $t('tasks.table.task_filters') }}
                  </span>
                </div>
              </div>
            </div>

            <div class="pl-3 pt-3 pb-3">
              <label class="">
                <input
                    type="radio"
                    class="mr-2"
                    v-model="selectedTaskType"
                    value="doc_collection_tasks"
                    @change="onTaskTypeChange"/>
                <span style="font-size: 13px">{{ $t('tasks.modal.data_collection_task') }}</span>
              </label><br />
              <label class="">
                <input
                    type="radio"
                    class="mr-2"
                    v-model="selectedTaskType"
                    value="main_tasks"
                    @change="onTaskTypeChange"/>
                <span style="font-size: 13px">{{ $t('tasks.modal.main_task') }}</span>
              </label>

              <!-- Switch Component -->
              <b-form-checkbox
                  v-model="taskLoaded"
                  name="check-button"
                  switch
                  class="icon-success radio-options"
                  style="display: flex; align-items: center;"
                  @input="loadProjectTasks(1, perPage)"
              >
                <span style="font-size: 13px">{{ $t('tasks.modal.filter_completed') }}</span>
              </b-form-checkbox>
            </div>
          </section>
        </div>

        <!-- RIGHT (60%) -->
        <div class="col-lg-7">
          {{ $t('tasks.table.task_list_title') }}

          <!-- TASKS SECTION -->
          <div style="padding: 16px 0">
            <!-- Task table -->
            <div
                v-if="open_tasks && open_tasks.length"
                class="card-list"
            >
              <div
                  v-for="(object, index) in open_tasks"
                  :key="index"
                  class="card-row cursor-pointer"
              >

                <!-- Row icon -->
                <div class="logo-container">
                  <span
                      v-if="(selectedTaskType === 'main_tasks' && object.status === 'completed') || (selectedTaskType === 'doc_collection_tasks' && object.data_status === 'completed')"
                      class="pr-2"
                      v-b-tooltip.hover
                      :title="$t('system.completed')"
                  >
                    <font-awesome-icon icon="check-circle" class="icon-success"/>
                  </span>
                  <span
                      v-else
                      class="pr-2"
                      v-b-tooltip.hover
                      :title="$t('system.pending')"
                  >
                    <font-awesome-icon
                        icon="exclamation-triangle"
                        class="risk-6"
                    />
                  </span>
                </div>

                <div class="card-row__main">
                  <!-- Main title -->
                  <div class="card-row__title">
                    <span v-if="object.code === 'update_measures'">
                      {{ $t('projects.measures.system_description_title') }}
                    </span>
                    <span v-else>
                      {{ object.reference }}: {{ object.title }}
                    </span>
                  </div>

                  <!-- Secondary title -->
                  <div
                      v-if="selectedTaskType === 'main_tasks'"
                      class="section-compact__secondary"
                  >
                    <span class="mr-1" v-for="(user, index) in object.users" :key="index">
                      <span data-qid="profile-photo">
                        <Avatar :object="user" size="xs"></Avatar>
                      </span>
                    </span>
                  </div>
                  <div
                      v-if="selectedTaskType === 'doc_collection_tasks' && selectedProject.type === 'control_assessment'"
                      class="section-compact__secondary"
                  >
                    <span class="mr-1" v-for="(user, index) in object.data_users" :key="index">
                      <span data-qid="profile-photo">
                        <Avatar :object="user" size="xs"></Avatar>
                      </span>
                    </span>
                  </div>
                </div>

                <!-- Action buttons -->
                <div class="card-row__right profile-compact__actions">
                  <b-button
                      v-if="selectedTaskType === 'main_tasks' && object.status !== 'completed'"
                      variant="link"
                      class="icon-btn"
                      qid="re-assign task"
                      @click="showEditUsersModal(object)"
                      v-b-tooltip.hover
                      :title="$t('tasks.re_assign_button')"
                  >
                    <font-awesome-icon icon="user-tag" />
                  </b-button>
                  <b-button
                      v-if="selectedTaskType === 'doc_collection_tasks' && (object.status !== 'completed' && object.data_status !== 'completed')"
                      variant="link"
                      class="icon-btn"
                      qid="re-assign task"
                      @click="showEditUsersModal(object)"
                      v-b-tooltip.hover
                      :title="$t('tasks.re_assign_button')"
                  >
                    <font-awesome-icon icon="user-tag" />
                  </b-button>

                  <AssessmentTaskModal
                      class=""
                      :task="object"
                      :tasks="selectedProject"
                      :fromHomepage="true"
                      @reloadContent="loadProjectTasks()"
                      v-if="(selectedProject.type === 'risk_assessment' || selectedProject.type === 'bia_assessment') && ((object.status === 'pending' && !isDataUser(object)) || ((object.data_status === 'pending' || !object.data_status) && isDataUser(object)))"
                  ></AssessmentTaskModal>
                  <AssessmentGlobalSingleTaskModal
                      class=""
                      :task="object"
                      :tasks="selectedProject"
                      :fromHomepage="true"
                      :selectedTaskType="selectedTaskType"
                      @reloadContent="loadProjectTasks(tasksCurrentPage)"
                      v-if="selectedProject.type === 'control_assessment' && ((object.status === 'pending' && !isDataUser(object)) || ((object.data_status === 'pending' || !object.data_status) && isDataUser(object)))"
                  ></AssessmentGlobalSingleTaskModal>
                  <RiskTaskModal
                      class=""
                      :task="object"
                      :tasks="selectedProject"
                      :fromHomepage="true"
                      @reloadContent="loadProjectTasks()"
                      v-if="(selectedProject.type === 'control' || selectedProject.type === 'security_measure') && ((object.status === 'pending' && !isDataUser(object)) || ((object.data_status === 'pending' || !object.data_status) && isDataUser(object)))"
                  ></RiskTaskModal>
                  <UpdateTaskModal
                      class=""
                      :task="object"
                      :tasks="selectedProject"
                      :fromHomepage="true"
                      @reloadContent="loadProjectTasks()"
                      v-if="selectedProject.type === 'update' && ((object.status === 'pending' && !isDataUser(object)) || ((object.data_status === 'pending' || !object.data_status) && isDataUser(object)))"
                  ></UpdateTaskModal>
                  <ValidationTaskModal
                      class=""
                      :task="object"
                      :tasks="selectedProject"
                      :fromHomepage="true"
                      @reloadContent="loadProjectTasks()"
                      v-if="selectedProject.type === 'validation' && ((object.status === 'pending' && !isDataUser(object)) || ((object.data_status === 'pending' || !object.data_status) && isDataUser(object)))"
                  ></ValidationTaskModal>

                  <b-button
                      v-if="(selectedTaskType === 'main_tasks' && object.status === 'completed') || (selectedTaskType === 'doc_collection_tasks' && object.data_status === 'completed')"
                      variant="link"
                      class="icon-btn"
                      qid="view task"
                      @click="showViewTaskModal(object)"
                      v-b-tooltip.hover
                      :title="$t('tasks.view_button')"
                  >
                    <font-awesome-icon icon="eye" />
                  </b-button>
                </div>
              </div>
            </div>

            <div v-if="open_tasks && !open_tasks.length && !loadingTasks">
              <div data-qid="no-results-row" class="text-center">
                <div colspan="10">
                  <h5 class="mt-3">
                    {{ $t('system.no_results') }}
                  </h5>
                </div>
              </div>
            </div>

            <div v-if="loadingTasks">
              <div data-qid="no-results-row-rr" class="text-center">
                <div colspan="10">
                  <div class="mt-2 mb-4">
                    <div class="swing-container">
                      <div class="swing">
                        <div class="swing-l"></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div class="swing-r"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pagination row">
            <div class="col-md-4">
              <div class="row">
                <div class="float-left pl-4">
                  <multiselect
                      v-model="tasksPerPage"
                      :options="perPageOptions"
                      track-by="value"
                      label="value"
                      select-label=""
                      selected-label=""
                      deselect-label=""
                      qid="create-org-form-organization-level"
                      @select="tasksPerPageChanged"
                      :placeholder="tasksSelectedPerPage"
                      @remove="tasksPerPageRemoved"
                  ></multiselect>
                </div>
              </div>
            </div>
            <div class="col-md-8">
              <b-pagination
                  class="float-right"
                  v-model="tasksCurrentPage"
                  :total-rows="tasksRows"
                  :per-page="tasksSelectedPerPage"
                  aria-controls="my-table-1"
                  @change="tasksPageChanged"
                  limit="1"
              >
                <span class="text-muted" slot="first-text"><font-awesome-icon icon="angle-double-left"/></span>
                <span class="text-muted" slot="prev-text"><font-awesome-icon icon="angle-left"/></span>
                <span class="text-muted" slot="next-text"><font-awesome-icon icon="angle-right"/></span>
                <span class="text-muted" slot="last-text"><font-awesome-icon icon="angle-double-right"/></span>
                <div slot="ellipsis-text">
                  <b-spinner small type="grow"></b-spinner>
                  <b-spinner small type="grow"></b-spinner>
                  <b-spinner small type="grow"></b-spinner>
                </div>
                <span class="text-muted" slot="page" slot-scope="{ page, active }">
                  <span v-if="active">{{getTasksPerPage}}</span>
                </span>
              </b-pagination>
            </div>
          </div>
        </div>
      </div>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
                <!-- Switch Component -->
                <b-form-checkbox
                    v-model="taskLoaded"
                    name="check-button"
                    switch
                    class="icon-success"
                    @input="loadProjectTasks(1, perPage)"
                >
                  <span class="text-white">{{ $t('tasks.modal.filter_completed') }}</span>
                </b-form-checkbox>
              </div>
              <div class="col-sm-6">
                <div class="text-right">
                  <b-button class="mt-3"
                            variant="secondary"
                            @click="hideViewProjectModal"
                            qid="create-rr-cancel-button"
                  >
                    {{ $t('system.close') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>


    <!-- RE-ASSIGN MODAL -->
    <b-modal ref="re-assign" @hide="hideEditUsersModal" modal-class="task-modal">
      <template #modal-header="{ close }">
        <div class="" style="width: 100%;">
          <span @click="hideEditUsersModal" class="btn btn-secondary cursor-pointer float-right">
            <font-awesome-icon icon="times"/>
          </span>

          <div class="d-block text-center">
            <h4 data-qid="re_assign-users-title">
              {{ $t('tasks.re_assign.title') }}
            </h4>
          </div>
        </div>
      </template>

      <b-alert v-if="error.length"
               show
               variant="danger"
               qid="re_assign-users-error"
      >
        {{ error }}
      </b-alert>

      <b-form @submit="reassignUsers" qid="re_assign-users-form" v-if="selectedTask && selectedTask.model">
        <div class="form-container">
          <b-form-group>
            <span v-if="isOwner(selectedTask) && selectedTaskType === 'main_tasks'">
              <label>
                {{ $t('tasks.table.task_responsible') }}
              </label>

              <div class="multiselect-row">
                <multiselect
                    v-model="selectedAssignUsers"
                    :options="allUsers"
                    :disabled="buttonDisabled"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="slug"
                    :placeholder="$t('projects.create.owners_placeholder')"
                    label="name"
                    qid="re_assign-users-select"
                >
                  <template slot="tag" slot-scope="props">
                    <span class="mr-3 user-tag">
                      <Avatar :object="props.option" size="xs"></Avatar>
                      <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                    </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                      {{ props.option.name }}
                    </span>
                  </template>
                </multiselect>

                <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
                ></UserInviteModal>
              </div>
              <br>
            </span>

            <span v-if="isDataOwner(selectedTask) && selectedTaskType === 'doc_collection_tasks'">
              <label data-qid="re_assign-data-users-label">
                {{ $t('tasks.table.data_collection_assignee') }}
              </label>

              <div class="multiselect-row">
                <multiselect
                    v-model="selectedAssignDataUsers"
                    :options="allUsers"
                    :disabled="buttonDisabled"
                    :multiple="true"
                    :taggable="false"
                    :select-label="$t('system.dropdown_select_label')"
                    :selected-label="$t('system.dropdown_selected_label')"
                    :deselect-label="$t('system.dropdown_deselect_label')"
                    track-by="slug"
                    :placeholder="$t('projects.create.owners_placeholder')"
                    label="name"
                    qid="re_assign-users-select"
                >
                  <template slot="tag" slot-scope="props">
                      <span class="mr-3 user-tag">
                        <Avatar :object="props.option" size="xs"></Avatar>
                        <span class="ml-2">
                          {{ props.option.name }}
                        </span>
                      </span>
                  </template>
                  <template slot="option" slot-scope="props">
                    <Avatar :object="props.option" size="xs"></Avatar>
                    <span class="ml-2">
                        {{ props.option.name }}
                      </span>
                  </template>
                </multiselect>

                <UserInviteModal
                    :buttonType="'small'"
                    :buttonDisabled=buttonDisabled
                    @reloadUserList="reloadUserList"
                ></UserInviteModal>
              </div>
            </span>
          </b-form-group>
        </div>
      </b-form>

      <template #modal-footer="{ close }">
        <div class="" style="width: 100%;">
          <div class="alert alert-menu-color color-secondary mb-0" role="alert">
            <div class="row">
              <div class="col-sm-6">
              </div>
              <div class="col-sm-6 text-right">
                <b-button class="mt-3"
                          variant="secondary"
                          @click="hideEditUsersModal"
                          qid="re_assign-users-cancel-button"
                >
                  {{ $t('projects.create.cancel') }}
                </b-button>
                <b-button type="submit"
                          class="mt-3 ml-3"
                          variant="success"
                          :disabled="buttonDisabled"
                          @click="reassignUsers"
                          qid="re_assign-users-submit-button"
                >
                  <span v-if="buttonDisabled" class="mr-1">
                    <font-awesome-icon icon="spinner" size="sm" class="fa-spin"/>
                  </span>
                  {{ $t('projects.create.submit') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-modal>

    <AssessmentTaskModal
        ref="routeAssessmentTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        style="display:none;"
    />
    <AssessmentGlobalSingleTaskModal
        ref="routeAssessmentGlobalTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        style="display:none;"
    />
    <RiskTaskModal
        ref="routeRiskTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        style="display:none;"
    />
    <UpdateTaskModal
        ref="routeUpdateTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        style="display:none;"
    />
    <ValidationTaskModal
        ref="routeValidationTaskModal"
        :task="selectedTask"
        :tasks="selectedTask && selectedTask.project ? selectedTask.project : {}"
        :fromHomepage="true"
        style="display:none;"
    />

    <TaskViewModal :selectedTask="selectedTask" :showModal="viewTaskModal" @closeViewModal="hideViewTaskModal" />
  </div>
</template>

<script>

import piincHttp from '@/util/configureAxios.js';
import roleCheck from '@/util/ACL/roleCheck';
import RejectedValidations from '@/components/RejectedValidations.vue';
import Avatar from '@/components/Avatar.vue';
import AssessmentTaskModal from '@/views/Tasks/AssessmentTaskModal.vue';
import AssessmentGlobalSingleTaskModal from '@/views/Tasks/AssessmentGlobalSingleTaskModal.vue';
import RiskTaskModal from '@/views/Tasks/RiskTaskModal.vue';
import UpdateTaskModal from '@/views/Tasks/UpdateTaskModal.vue';
import ValidationTaskModal from '@/views/Tasks/ValidationTaskModal.vue';
import TaskViewModal from '@/views/Tasks/TaskViewModal.vue';
import GuideButton from '@/components/guide/GuideButton.vue';
import CreateProjectModal from '@/views/Projects/CreateProjectModal.vue';
import ProjectViewModal from '@/views/Projects/ProjectViewModal.vue';
import UserInviteModal from '@/components/user/UserInviteModal.vue';
import CreatePartitionModal from '@/views/Partitions/CreatePartitionModal.vue';


// import VueCropper from 'vue-cropperjs';
import Cropper from 'cropperjs';
import 'cropperjs/dist/cropper.css';

export default {
  name: 'Home',
  data() {
    return {
      navigation: [],
      users_total: 0,
      users_position: 0,
      topUserListOpened: false,
      topUsers: {},
      organizations_total: 0,
      organizations_position: 0,
      topOrganizationListOpened: false,
      topOrganizations: [],
      loadingOrganizations: true,
      allUsers: [],
      statistics: {},
      myProjectsOpen: [],
      validations: [],
      loadingOpen: true,
      loadingOpenProjects: true,
      projectsShowMore: true,
      projectsPage: 1,
      loadingValidations: true,
      validationsShowMore: true,
      validationsPage: 1,
      taskForm: {
        remarks: ''
      },
      taskSwitchOptions: [
        {text: this.$t('system.yes'), value: 1},
        {text: this.$t('system.no'), value: 0},
      ],
      filterOpened: false,
      error: '',
      selectedTask: [],
      viewTaskModal: false,
      selectedAssignUsers: [],
      selectedAssignDataUsers: [],
      buttonDisabled: false,
      selectedFile: {},
      selectedLink: {},
      showDeleteAttachmentModal: false,
      showDeleteLinkModal: false,
      showInput: false,
      showSingleLinkLabel: true,
      inputLink: '',
      showFullProjectMessage: false,
      showFullGenericControlDescription: false,
      tabIndex: 0,
      showButton: false,
      avatar: '',
      avatarLarge: '',
      imageToCrop: '',
      userForm: {
        name: '',
        email: '',
        phone: '',
        file: {}
      },
      cropper: {},
      user: {},
      showNameInput: false,
      showPhoneInput: false,
      userName: '',
      userPhone: '',
      showValidationsTab: false,
      tableTasks: [],
      tableProjects: [],
      selectedProject: {},
      open_tasks: [],
      loadingTasks: false,
      unreadCount: 0,
      validationCount: 0,

      // Project progress modal
      selectedProjectSlug: '',
      viewProjectModal: false,

      // Pagination
      perPageOptions: [
        { name: '10', value: '10' },
        { name: '20', value: '20' },
        { name: '50', value: '50' },
        { name: '100', value: '100' }
      ],

      //Project pagination
      selectedPerPageProjects: '10',
      currentPageProjects: 1,
      totalRowsProjects: 0,
      perPageProjects: '',

      //Task pagination
      selectedPerPage: '10',
      currentPage: 1,
      totalRows: 0,
      perPage: '',

      // Task modal pagination
      tasksSelectedPerPage: '10',
      tasksCurrentPage: 1,
      tasksTotalRows: 0,
      tasksPerPage: '',

      taskLoaded: 0,

      // Task filter
      filters: {
        type: [],
        scope: '',
        status: ''
      },
      types: [
        { name: this.$t('projects.view.update_label'), value: 'update' },
        { name: this.$t('projects.view.validation_label'), value: 'validation' },
        { name: this.$t('projects.view.control_label'), value: 'control' },
        { name: this.$t('projects.view.security_measure_label'), value: 'security_measure' },
        { name: this.$t('maintenance.assessments.type.risk_assessment'), value: 'risk_assessment' },
        { name: this.$t('maintenance.assessments.type.control_assessment'), value: 'control_assessment' },
        { name: this.$t('maintenance.assessments.type.bia_assessment'), value: 'bia_assessment' }
      ],
      scopes: [
        { name: this.$t('projects.scope.policy'), value: 'policy' },
        { name: this.$t('projects.system.label'), value: 'system' },
        { name: this.$t('projects.process.label'), value: 'process' },
        { name: this.$t('projects.activity.label'), value: 'activity' },
        { name: this.$t('projects.third_party.label'), value: 'third_party' }
      ],
      statuses: [
        { name: this.$t('system.completed'), value: 'completed' },
        { name: this.$t('system.in_progress'), value: 'in progress' }
      ],
      sortColumn: 'name',
      sortOrder: 'asc',
      taskType: 'main',
      selectedTaskType: '',
      measuresFound: true,
      routeTaskHandledKey: '',
      routeTaskOpening: false,
    };
  },
  components: {
    Avatar,
    AssessmentTaskModal,
    AssessmentGlobalSingleTaskModal,
    RiskTaskModal,
    UpdateTaskModal,
    ValidationTaskModal,
    RejectedValidations,
    TaskViewModal,
    GuideButton,
    CreateProjectModal,
    ProjectViewModal,
    UserInviteModal,
    CreatePartitionModal,
  },
  watch: {
    // Watch when client changes in store and trigger api reload
    getClient: function () {
      this.trialExpiredRedirect()
      this.loadNavigation()
      this.loadProjects()
      this.loadTasks()
      if (this.getUser && this.getUser.slug) {
        this.loadUser()
      }
      this.avatarLarge = this.getProfileAvatar;
      this.loadCount()
      this.loadValidationCount()
      this.openTaskFromRouteIfNeeded()
    },
    getReloadPage: function () {
      this.loadProjects()
      this.loadTasks()
    },
    getUser: function (newValue) {
      if (newValue && newValue.slug) {
        if (this.getClient && this.getClient.slug) {
          this.loadUser()
        }
        this.openTaskFromRouteIfNeeded()
      }
    },
    '$route.fullPath': function () {
      if (!this.$route.query.task_id) {
        this.routeTaskHandledKey = ''
      }
      this.openTaskFromRouteIfNeeded()
    },
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    getProfileAvatar(){
      let user = this.user;
      if (user.avatar_large) {
        return user.avatar_large;
      } else {
        return require('@/assets/avatar.png');
      }
    },
    getReloadPage() {
      return this.$store.state.updatePage
    },
    getUserClients() {
      return this.$store.state.user.clients
    },
    getStorePerPage() {
      return this.$store.state.perPage
    },
    getPerPage() {
      let page = this.currentPage;
      let selectedFrom = (page-1) * Number(this.selectedPerPage) + 1;
      let selectedTo = (page-1) * Number(this.selectedPerPage) + Number(this.selectedPerPage);
      let selectedOf = this.totalRows;

      if (selectedTo > selectedOf) {
        selectedTo = selectedOf
      }

      return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
    },
    getPerPageProjects() {
      let page = this.currentPageProjects;
      let selectedFrom = (page-1) * Number(this.selectedPerPageProjects) + 1;
      let selectedTo = (page-1) * Number(this.selectedPerPageProjects) + Number(this.selectedPerPageProjects);
      let selectedOf = this.totalRowsProjects;

      if (selectedTo > selectedOf) {
        selectedTo = selectedOf
      }

      return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
    },
    getTasksPerPage() {
      let page = this.tasksCurrentPage;
      let selectedFrom = (page-1) * Number(this.tasksSelectedPerPage) + 1;
      let selectedTo = (page-1) * Number(this.tasksSelectedPerPage) + Number(this.tasksSelectedPerPage);
      let selectedOf = this.tasksTotalRows;

      if (selectedTo > selectedOf) {
        selectedTo = selectedOf
      }

      return selectedFrom + " - " + selectedTo + " " + this.$t('system.of') + " " + selectedOf
    },
    rows() {
      // Use computed value for rows because of dynamic update
      return this.totalRows
    },
    rowsProjects() {
      // Use computed value for rows because of dynamic update
      return this.totalRowsProjects
    },
    tasksRows() {
      // Use computed value for rows because of dynamic update
      return this.tasksTotalRows
    }
  },
  methods: {
    sortBy(column) {
      // Toggle sort order if the same column is clicked again
      if (this.sortColumn === column) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = column;
        this.sortOrder = 'asc'; // Default to ascending order
      }

      this.loadProjectTasks(1,this.perPage,this.sortColumn, this.sortOrder)
    },
    isOwner(object) {
      let self = this;
      let allow = false;
      let existingUser = _.find(object.users, function(user) {
        return user.id === self.getUser.id;
      });

      if (existingUser) {
        allow = true;
      } else {
        if (roleCheck('Client admin') || roleCheck('Partition admin') || roleCheck('Validator') || this.getUser.isAdmin) {
          allow = true;
        }
      }

      return allow;
    },
    isDataOwner(object) {
      let self = this;
      return _.find(object.data_users, function (user) {
        return user.id === self.getUser.id;
      });
    },
    isDataUser(object) {
      let self = this;
      let existingUser = _.find(object.data_users, function(user) {
        return user.id === self.getUser.id;
      });

      let existingTaskUser = _.find(object.users, function(user) {
        return user.id === self.getUser.id;
      });

      if(existingTaskUser){
        return false;
      }

      return !!existingUser;
    },
    debounceInput: _.debounce(function() {
      this.loadTasks()
    }, 400),
    reloadContent() {
      this.loadProjects(this.currentPageProjects, this.selectedPerPageProjects)
      this.loadTasks(this.currentPage, this.selectedPerPage)
      this.loadCount()
      this.loadValidationCount()
      this.$store.commit('storeReloadCount');
    },
    resetFilters() {
      this.filters.type = []
      this.filters.scope = ''
      this.filters.status = ''

      this.debounceInput()
    },
    handleFilterButton(forceOpened) {
      if (forceOpened) {
        this.filterOpened = true
      } else {
        this.filterOpened = !this.filterOpened
      }
    },
    pageChanged(page) {
      this.loadTasks(page)
    },
    pageChangedProjects(page) {
      this.loadProjects(page)
    },
    perPageChanged(perPage) {
      let self = this;
      self.selectedPerPage = perPage.value
      this.$store.commit('storePerPage', this.selectedPerPage);
      // Load content when page changed listener is triggered
      this.loadProjects(1, perPage.value)
      this.loadTasks(1, perPage.value)
    },
    perPageChangedProjects(perPage) {
      let self = this;
      self.selectedPerPageProjects = perPage.value
      this.$store.commit('storePerPage', this.selectedPerPageProjects);
      // Load content when page changed listener is triggered
      this.loadProjects(1, perPage.value)
      this.loadTasks(1, perPage.value)
    },
    perPageRemoved() {
      let self = this;
      // Load content when page changed listener is triggered
      self.selectedPerPage = self.perPageOptions[0].value
      self.selectedPerPageProjects = self.perPageOptions[0].value
      this.loadTasks(1, self.selectedPerPage)
      this.loadProjects(1, self.selectedPerPageProjects)
    },
    tasksPageChanged(page) {
      this.loadProjectTasks(page)
    },
    tasksPerPageChanged(perPage) {
      let self = this;
      self.tasksSelectedPerPage = perPage.value
      this.$store.commit('storePerPage', this.tasksSelectedPerPage);
      // Load content when page changed listener is triggered
      this.loadProjectTasks(1, perPage.value)
    },
    tasksPerPageRemoved() {
      let self = this;
      // Load content when page changed listener is triggered
      self.tasksSelectedPerPage = self.perPageOptions[0].value
      this.loadProjectTasks(1, self.tasksSelectedPerPage)
    },
    showViewTaskModal(object) {
      this.selectedTask = object
      this.viewTaskModal = true
    },
    hideViewTaskModal() {
      this.selectedTask = {}
      this.viewTaskModal = false
    },
    getRouteTaskType(type) {
      return type === 'doc_collection_tasks' ? 'doc_collection_tasks' : 'main_tasks'
    },
    openDeepLinkedTaskModal(task) {
      if (!task || !task.project || !task.project.type) {
        return
      }

      let refName = ''

      if (task.project.type === 'risk_assessment' || task.project.type === 'bia_assessment') {
        refName = 'routeAssessmentTaskModal'
      } else if (task.project.type === 'control_assessment') {
        refName = 'routeAssessmentGlobalTaskModal'
      } else if (task.project.type === 'control' || task.project.type === 'security_measure') {
        refName = 'routeRiskTaskModal'
      } else if (task.project.type === 'update') {
        refName = 'routeUpdateTaskModal'
      } else if (task.project.type === 'validation') {
        refName = 'routeValidationTaskModal'
      }

      if (refName && this.$refs[refName] && typeof this.$refs[refName].showTaskModal === 'function') {
        this.$refs[refName].showTaskModal(task)
      } else {
        this.showViewTaskModal(task)
      }
    },
    openTaskFromRouteIfNeeded() {
      const taskId = this.$route.query.task_id

      if (!taskId || !this.getClient || !this.getClient.slug || !this.getUser || !this.getUser.id) {
        return
      }

      const routeTaskKey = `${this.getClient.slug}:${taskId}:${this.getRouteTaskType(this.$route.query.type)}`
      if (this.routeTaskOpening || this.routeTaskHandledKey === routeTaskKey) {
        return
      }

      this.routeTaskOpening = true

      piincHttp.get('tasks/' + taskId).then((response) => {
        const task = response.data

        if (!task || !task.id) {
          this.routeTaskOpening = false
          return
        }

        this.selectedTask = task
        this.selectedTaskType = this.getRouteTaskType(this.$route.query.type)
        this.routeTaskHandledKey = routeTaskKey

        this.$nextTick(() => {
          this.openDeepLinkedTaskModal(task)
          this.routeTaskOpening = false
        })
      }, () => {
        this.routeTaskOpening = false
      })
    },
    hideEditUsersModal() {
      this.selectedTask = {};
      this.selectedAssignUsers = [];
      this.selectedAssignDataUsers = [];
      this.$refs['re-assign'].hide();
    },
    reloadUserList() {
      let object = this.selectedTask

      this.$refs['re-assign'].hide();
      this.showEditUsersModal(object);
    },
    showEditUsersModal(object) {
      let self = this;
      let task = object;
      let role_name = [self.permissions.ROLE_CLIENT_ADMIN];

      this.error = '';
      this.buttonDisabled = true;
      this.loading = true;

      piincHttp.get('tasks/' + object.id).then(function(response) {
        task = response.data;

        if (task.project.type === 'control' || task.project.type === 'security_measure' || task.project.type === 'control_assessment') {
          role_name.push(self.permissions.ROLE_MEMBER);
          role_name.push(self.permissions.ROLE_VALIDATOR);
        }

        if (self.getClient && self.getClient.parent) {
          role_name.push(self.permissions.ROLE_PARTITION_ADMIN);
        }

        self.selectedAssignUsers = [];
        self.selectedAssignDataUsers = [];

        const params = {
          client: self.getClient.id,
          per_page: 100
        };

        if (task.code !== 'control_assessment') {
          params.role_name = role_name;
        }

        piincHttp.get('users', { params }).then(function(response) {
          let users = response.data.items;

          _.forEach(object.users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {

              users.push(user);

            }

            let matchedUser = _.cloneDeep(user);
            if (user.id === object.owner) {
              matchedUser.owner = true;
            }
            self.selectedAssignUsers.push(matchedUser);

          })

          _.forEach(object.data_users, function (user) {
            if (_.findIndex(users, function(o) { return o.id === user.id; }) === -1) {

              users.push(user);

            }

            let matchedUser = _.cloneDeep(user);
            if (user.id === object.owner) {
              matchedUser.owner = true;
            }
            self.selectedAssignDataUsers.push(matchedUser);

          })

          self.allUsers = _.uniq(users);
          self.buttonDisabled = false;
          self.loading = false;
        }, function() {
          self.buttonDisabled = false;
          self.loading = false;
        });
      }, function() {
        self.buttonDisabled = false;
        self.loading = false;
      });

      this.selectedTask = object;
      this.$refs['re-assign'].show();
    },
    reassignUsers(evt) {
      evt.preventDefault();

      this.buttonDisabled = true;

      if (this.isOwner(this.selectedTask)) {
        this.reassignFormSubmit();

        if (this.isDataOwner(this.selectedTask)) {
          this.reassignDataFormSubmit();
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        } else {
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        }
      } else {
        if (this.isDataOwner(this.selectedTask)) {
          this.reassignDataFormSubmit();
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        } else {
          this.hideEditUsersModal();
          // this.loadProjectTasks();
        }
      }

      this.buttonDisabled = false;
    },
    reassignFormSubmit() {
      //this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      let userIds = [];

      if (this.selectedAssignUsers && this.selectedAssignUsers.length) {
        for (var i = 0; i < this.selectedAssignUsers.length; i++) {
          userIds.push(this.selectedAssignUsers[i].id);
        }
      }

      if (userIds && userIds.length) {
        piincHttp.put('tasks/' + this.selectedTask.id, {
          users: userIds,
          reasign: 'replace'
        }).then(function() {
          // self.hideEditUsersModal();
          self.loadProjectTasks();
          self.$toastr('success', self.$t('tasks.re_assign.success'))
          // self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            // self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        self.error = self.$t('tasks.re_assign.required')
        //self.buttonDisabled = false;
      }
    },
    reassignDataFormSubmit() {
      //this.buttonDisabled = true;
      // Keep the reference in a variable
      // to use it inside function called when promise returned
      let self = this;
      let userIds = [];

      if (this.selectedAssignDataUsers && this.selectedAssignDataUsers.length) {
        for (var i = 0; i < this.selectedAssignDataUsers.length; i++) {
          userIds.push(this.selectedAssignDataUsers[i].id);
        }
      }

      if (userIds && userIds.length) {
        piincHttp.put('tasks/' + this.selectedTask.id, {
          data_users: userIds,
          reasign: 'replace'
        }).then(function() {
          // self.hideEditUsersModal();
          self.loadProjectTasks();
          self.$toastr('success', self.$t('tasks.re_assign.success'))
          // self.buttonDisabled = false;
        }, function(error) {
          if (error.status !== 200) {
            // self.buttonDisabled = false;
            self.error = error.data.message;
          }
        })
      } else {
        self.error = self.$t('tasks.re_assign.required')
        //self.buttonDisabled = false;
      }
    },
    loadProjectTasks(page, perPage, sort = 'order', order = 'asc') {
      let self = this;
      this.loadingTasks = true;

      let status = ['pending']
      if(this.taskLoaded){
        status = ['pending','completed']
      }

      if (this.getStorePerPage) {
        this.tasksSelectedPerPage = this.getStorePerPage;
      }

      // If page is not defined, set page to default 1
      let perPageNumber = perPage ? perPage : this.tasksSelectedPerPage;

      let pageNumber = 1; //page ? page : 1;
      if (page) {
        pageNumber = page ? page : 1;
        this.tasksCurrentPage = page;
      }else{
        pageNumber = 1;
        this.tasksCurrentPage = 1;
      }

      piincHttp.get('projects/' + this.selectedProject.slug + '/tasks/me/small', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              status: status,
              sort: sort,
              order: order,
              type: this.selectedTaskType
            }
      }).then(function(response) {
        // self.open_tasks = []
        self.open_tasks = response.data.items;
        // Set number of total items
        self.tasksTotalRows = response.data.total_count;
        self.loadingTasks = false;
      }, function() {});
    },
    openViewProjectModal(object) {
      this.selectedProject = object;

      if( this.selectedProject.type === "control_assessment"){
        this.selectedTaskType = 'doc_collection_tasks';
      }else{
        this.selectedTaskType = 'main_tasks'
      }

      this.loadProjectTasks();
      this.$refs['view-project'].show();
    },
    hideViewProjectModal() {
      this.selectedProject = {};
      this.open_tasks = [];
      this.tasksPerPage = '';
      this.currentPage = 1
      this.tasksCurrentPage = 1;
      this.tasksTotalRows = 0;

      this.$refs['view-project'].hide();

      this.loadProjects()
      this.loadTasks()
      this.loadCount()
    },
    showNameInputFunction() {
      this.showNameInput = true
      this.userName = this.user.name
      let self = this
      setTimeout(function(){
        self.$refs.nameInput.$el.focus();
      }, 100);
    },
    hideNameInputFunction() {
      this.showNameInput = false
    },
    validationsTabClicked() {
      this.showValidationsTab = true;
    },
    saveName() {
      let self = this;
      if (this.userName) {
        piincHttp.put('users/' + self.getUser.slug, {
          name: this.userName
        }).then(function(response) {

          response.data.clients = self.getUserClients;
          // Store returned user in store if my profile
          self.$store.commit('storeUser', response.data);
          self.showNameInput = false;
          self.loadUser()
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      }

    },
    showCropModal() {
      let sizeError = false;
      let self = this

      this.$refs['crop-image'].show()
      this.buttonDisabled = false;

      setTimeout(function(){
        const image = document.getElementById('crop');
        const cropper = new Cropper(image, {
          aspectRatio: 1,
          viewMode: 2,
          crop(event) {

          },
        });

        document.getElementById('crop-button').addEventListener('click', function(){
          self.buttonDisabled = true;

          cropper.getCroppedCanvas().toBlob(function (blob) {
            var formData = new FormData();
            formData.append('croppedImage', blob);

            if (blob.size > 10485760) {
              sizeError = true;
            }

            formData.append("avatar", blob);
            formData.append("_method", 'put');

            if (sizeError) {
              self.error = self.$t('system.file_size')
              alert(self.error)
            } else {
              piincHttp.post('users/' + self.getUser.slug, formData, {
                headers: {
                  'Content-Type': 'multipart/form-data'
                }
              }).then(function(response) {
                response.data.clients = self.getUserClients;
                // Store returned user in store if my profile
                self.$store.commit('storeUser', response.data);
                self.hideCropModal()
              }, function(error) {
                self.buttonDisabled = false;
                self.userForm.error = error.data.message;
                if (error.data.errors.avatar) {
                  self.userForm.error += ' ' + error.data.errors.avatar[0]
                }
                alert(self.userForm.error)
              })
            }

          });

        })
      }, 500);

    },
    hideCropModal() {
      this.loadUser()
      this.$refs['crop-image'].hide()
    },
    onFileChange(file) {
      this.userForm.file = file;
      const avatar = file.target.files[0];
      this.avatar = URL.createObjectURL(avatar);
      this.avatarLarge = URL.createObjectURL(avatar);
      this.imageToCrop = URL.createObjectURL(avatar);

      this.showCropModal()

    },
    resetFile() {
      let imagefile = document.querySelector('#file-photo');
      imagefile.value = '';
    },
    downloadMedia(object){
      piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = object.file_name;

        link.click();
        link.remove()
        window.URL.revokeObjectURL(url);
      }, function() {});
    },
    getDifference(endDate) {
      const moment = require('moment');
      const currentDate = moment()
      endDate = moment(endDate);
      return endDate.diff(currentDate, 'days');
    },
    loadTasks(page, perPage) {
      let formTypes = [];

      if (this.getStorePerPage) {
        this.selectedPerPage = this.getStorePerPage;
      }

      if (this.filters.type && this.filters.type.length) {
        for (var t = 0; t < this.filters.type.length; t++) {
          formTypes[t] = this.filters.type[t].value;
        }
        this.handleFilterButton(true)
      }

      // If page is not defined, set page to default 1
      let pageNumber = page ? page : 1;
      let perPageNumber = perPage ? perPage : this.selectedPerPage;
      let self = this;

      this.loadingOpen = true;
      piincHttp.get('projects/me', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              status: this.filters.status ? this.filters.status.value : 'in progress',
              type: formTypes ? formTypes : undefined,
              scope: this.filters.scope ? this.filters.scope.value : undefined,
              sort: 'end_date',
              order: 'asc',
              client: this.getClient.slug
            }
      }).then(function(response) {

        self.tableTasks = response.data.items;
        // Set number of total items
        self.totalRows = response.data.total_count;
        self.loadingOpen = false;
      }, function() {});
    },
    loadProjects(page, perPage) {
      if (this.getStorePerPage) {
        this.selectedPerPageProjects = this.getStorePerPage;
      }

      // If page is not defined, set page to default 1
      let pageNumber = page ? page : 1;
      let perPageNumber = perPage ? perPage : this.selectedPerPageProjects;
      let self = this;

      this.loadingOpenProjects = true;
      piincHttp.get('projects', { params:
            {
              page: pageNumber,
              per_page: perPageNumber,
              status: ['in progress', 'pending'],
              sort: 'end_date',
              control_type: 'one time',
              order: 'asc',
              client: this.getClient.slug,
              owners: [this.getUser.id]
            }
      }).then(function(response) {

        self.tableProjects = response.data.items;
        // Set number of total items
        self.totalRowsProjects = response.data.total_count;
        self.loadingOpenProjects = false;
      }, function() {});

      if (this.getClient && this.getClient.parent) this.checkMeasures();
    },
    loadUser(){
      let self = this;
      piincHttp.get('users/' + this.getUser.slug, { params:
            {
              client: this.getClient.slug
            }
      }).then(function(response) {
        self.user = response.data;
      }, function() {});
    },
    loadCount() {
      let self = this;
      if (this.getClient.projectModule) {
        piincHttp.get('projects/me/tasks', { params:
              {
                count: 1,
                status: 'pending',
                client: this.getClient.slug
              }
        }).then(function (response) {
          self.unreadCount = response.data.total_count;
        }, function () {});
      }
    },
    loadValidationCount() {
      let self = this;
      piincHttp.get('validations/rejected', { params:
            {
              count: 1,
              client: this.getClient.slug
            }
      }).then(function(response) {
        self.loading = false;
        self.validationCount = response.data.total_count;
      }, function() {});

    },
    trialExpiredRedirect() {
      var moment = require('moment');
      let self = this;

      setTimeout(function(){
        let currentDate = moment()
        let endDate = moment(self.getClient.trial_ends_at);
        let difference = endDate.diff(currentDate, 'days');

        if ((self.getClient.subscription_status === 'trial_expired' && difference < 1) || self.getClient.subscription_status === 'subscription_ended') {
          self.$router.push({ name: 'clientProfile', params: { slug: self.getClient.slug }, query: { tabindex: 1 }})
        }
      }, 500);

    },
    showTask(object) {
      this.selectedProjectSlug = object.slug;
      this.viewProjectModal = true;
    },
    closeViewProjectModal(statusChanged) {
      this.selectedProjectSlug = '';
      this.viewProjectModal = false;

      if (statusChanged) {
        this.loadProjects();
        this.loadTasks();
      }
    },
    loadNavigation() {
      let self = this;
      piincHttp.get('users/me/navigation', { params:
            {
              client: this.getClient.slug
            }
      }).then(function (response) {
        self.navigation = response.data;
      }, function () {});
    },
    onTaskTypeChange() {
      this.loadProjectTasks(1, this.tasksPerPage, 'order', 'asc');
    },
    /**
     * Returns a CSS class for the due-date pill based on days remaining.
     * Uses existing getDifference(date) which returns (date - today) in days.
     */
    dueClass(date) {
      if (!date) return 'due--none'

      // guard against bad dates
      const diff = this.getDifference(date)
      if (diff === null || diff === undefined || Number.isNaN(diff)) return 'due--none'

      // overdue
      if (diff < 0) return 'due--overdue'

      // due soon (<= 3 days)
      if (diff <= 3) return 'due--soon'

      // normal
      return 'due--ok'
    },
    checkMeasures() {
      let self = this;

      self.measuresFound = true;

      piincHttp.get('measure-categories', { params:
            {
              per_page: 1,
              client: self.getClient.slug
            }
      }).then(function(categories_response) {
        if (categories_response.data.total_count) {
          self.measuresFound = true;
        }else{
          piincHttp.get('measures', { params:
                {
                  page: 1,
                  per_page: 1,
                  client: self.getClient.slug
                }
          }).then(function(measure_response) {
            self.measuresFound = !!measure_response.data.total_count;
          }, function() {});
        }
      }, function() {});
    },
  },
  created() {
    if (this.getClient && this.getClient.slug) {

      this.trialExpiredRedirect()
      this.loadNavigation()
      this.loadValidationCount()

      if (this.getUser && this.getUser.slug) {
        this.loadUser()
      }

      if (this.getClient.projectModule) {
        this.loadProjects()
        this.loadTasks()
        this.loadCount()
      }
    }
    this.openTaskFromRouteIfNeeded()
  },
  mounted () {
    let topElement = document.getElementById("app");
    if (topElement) {
      topElement.classList.remove("sidebar-collapsed");
    }
  }
}

</script>
